import React, { useEffect, useMemo, useState } from "react";
import "../fonts/breuer-headline.css";
import "./rules.css";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { selectAdminToken } from "../../admin/sessionSlice";
import { setBackButtonUrl } from "../uiSlice";
import { resolveGameEntryRoute } from "../../functions/stageReportSync";
import { setupAppPageBodyBackground } from "../game/gameStageBackground";
import logoImg from "../../img/logo.png";

const STORY_HEADING = "Alas! What a National Catastrophe!";
const STORY_BODY =
  "Our Constitution has been stolen just a week before we celebrate Republic Day. Investigation leads us to believe it's a thief who has been operating under wraps for a year to plan this heist. He, along with his troupe, have either worked hand in glove or coerced the security team with personal threats to make the impossible happen. It's your job as National Guards to bring back our Constitution before the leak of treasurable information can take place";

function UserRules() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { data: themeData } = useSelector((state) => state.theme);
  const { user } = useSelector((state) => state.auth);
  const adminToken = useSelector(selectAdminToken);
  const [nextLoading, setNextLoading] = useState(false);

  const backendBase = String(process.env.REACT_APP_BACKEND_URL || "").replace(
    /\/+$/,
    ""
  );

  useEffect(() => {
    dispatch(setBackButtonUrl("/login?&save=true"));
  }, [dispatch]);

  useEffect(() => {
    return setupAppPageBodyBackground(document.body);
  }, []);

  const storyHeading = useMemo(() => {
    const raw = themeData?.landing_page_title ?? themeData?.main_title;
    const t = String(raw || "").trim();
    if (!t || /pick, decide/i.test(t) || /are you in it/i.test(t)) {
      return STORY_HEADING;
    }
    return t;
  }, [themeData]);

  const storyBody = useMemo(() => {
    const raw =
      themeData?.rules_intro ??
      themeData?.story_text ??
      themeData?.landing_page_subtitle;
    const t = String(raw || "").trim();
    return t || STORY_BODY;
  }, [themeData]);

  const nextButtonTextColor = useMemo(() => {
    const raw = themeData?.button_Textcolor ?? themeData?.button_text_color;
    if (raw == null || raw === "") return "#ffffff";
    return String(raw).trim() || "#ffffff";
  }, [themeData]);

  const nextButtonBackground = useMemo(() => {
    const raw = themeData?.begin_button_color ?? themeData?.button_color;
    if (raw == null || String(raw).trim() === "") return "#e9695e";
    return String(raw).trim();
  }, [themeData]);

  const handleNextClick = async (e) => {
    e.preventDefault();
    if (nextLoading) return;

    // After refresh Redux auth is empty; sessionStorage still has the login payload.
    let storedUser = user;
    try {
      const raw = sessionStorage.getItem("userData");
      if (raw) {
        const parsed = JSON.parse(raw);
        if (parsed && typeof parsed === "object") {
          storedUser = { ...(user || {}), ...parsed };
        }
      }
    } catch {
      storedUser = user;
    }

    const uid =
      storedUser?.userId ?? storedUser?.userid ?? storedUser?.id ?? null;
    if (!uid) {
      navigate("/login", { replace: true });
      return;
    }

    const token =
      adminToken ||
      (storedUser?.sessionId && storedUser?.organizationId
        ? `${storedUser.sessionId}&${storedUser.organizationId}`
        : null);

    setNextLoading(true);
    try {
      const route = await resolveGameEntryRoute({
        backendBase,
        adminToken: token,
        storedUser,
        isDemoBypass:
          storedUser.sessionId === "demobypass" ||
          storedUser.role === "demobypass",
      });
      navigate(route, { replace: true });
    } catch (err) {
      console.error("rules handleNextClick:", err);
      navigate("/stage1", { replace: true });
    } finally {
      setNextLoading(false);
    }
  };

  return (
    <div className="user-rules-page">
      <div className="user-rules-stack">
        <img
          src={logoImg}
          className="user-rules-brand"
          alt="National Heist"
          draggable={false}
        />

        <h1 className="user-rules-story-title">{storyHeading}</h1>
        <p className="user-rules-story-body">{storyBody}</p>

        <button
          type="button"
          className="user-next-button"
          onClick={handleNextClick}
          disabled={nextLoading}
          style={{
            backgroundColor: nextButtonBackground,
            color: nextButtonTextColor,
          }}
        >
          {nextLoading ? "Loading…" : "Next"}
        </button>
      </div>
    </div>
  );
}

export default UserRules;
